
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Bike } from '@/services/types';
import { useQuery } from '@tanstack/react-query';

interface SearchResultsProps {
  searchQuery: string;
  onBikeSelect: (bike: Bike) => void;
}

const searchBikes = async (query: string): Promise<Bike[]> => {
  // Импортируем сервис поиска динамически
  const { fetchAllBikes } = await import('@/pages/admin/bikes/services/bikePaginationService');
  
  const result = await fetchAllBikes();
  
  // Фильтруем результаты по поисковому запросу на стороне клиента
  const searchLower = query.toLowerCase();
  const filteredBikes = result.bikes.filter(bike => {
    return (
      bike.serialNumber?.toLowerCase().includes(searchLower) ||
      bike.model?.toLowerCase().includes(searchLower) ||
      bike.gps_tag?.toLowerCase().includes(searchLower) ||
      bike.courierName?.toLowerCase().includes(searchLower) ||
      bike.amo_number?.toLowerCase().includes(searchLower)
    );
  });
  
  return filteredBikes;
};

export const SearchResults: React.FC<SearchResultsProps> = ({
  searchQuery,
  onBikeSelect
}) => {
  const { data: bikes = [], isLoading, error } = useQuery({
    queryKey: ['quick-bike-search', searchQuery],
    queryFn: () => searchBikes(searchQuery),
    enabled: !!searchQuery && searchQuery.length > 2
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-sm text-muted-foreground">Поиск велосипедов...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-sm text-destructive">Ошибка поиска: {String(error)}</div>
      </div>
    );
  }

  if (bikes.length === 0) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-sm text-muted-foreground">Велосипеды не найдены</div>
      </div>
    );
  }

  return (
    <ScrollArea className="flex-1">
      <div className="space-y-2">
        {bikes.map((bike) => (
          <Card 
            key={bike.id} 
            className="cursor-pointer hover:bg-accent transition-colors"
            onClick={() => onBikeSelect(bike)}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <div className="font-medium">
                    {bike.model} ({bike.serialNumber})
                  </div>
                  <div className="text-sm text-muted-foreground">
                    GPS: {bike.gps_tag || 'Не указан'}
                    {bike.courierName && ` • Курьер: ${bike.courierName}`}
                    {bike.rental_human_number && ` • Сделка: ${bike.rental_human_number}`}
                  </div>
                </div>
                <div className="flex flex-col items-end space-y-1">
                  <Badge variant={bike.status === 'available' ? 'default' : 'secondary'}>
                    {bike.status === 'available' ? 'Доступен' : 
                     bike.status === 'in_use' ? 'В аренде' : 'Обслуживание'}
                  </Badge>
                  {bike.movement_blocked && (
                    <Badge variant="destructive" className="text-xs">
                      Заблокирован
                    </Badge>
                  )}
                  {bike.speed_limit && (
                    <Badge variant="outline" className="text-xs">
                      {bike.speed_limit} км/ч
                    </Badge>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </ScrollArea>
  );
};
